import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type ExcelJS from 'exceljs';
import { TodayArrivalsReportData } from '../services/citasService.ts';

const BRAND_BLUE: [number, number, number] = [0, 127, 200];
const BRAND_NAVY: [number, number, number] = [15, 23, 42];
const LIGHT_BLUE = 'EAF6FC';
const BORDER = 'D9E2EC';

const cleanFilePart = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .toLowerCase();

const branchName = (report: TodayArrivalsReportData, selectedBranch?: string) => {
  const branches = Array.from(new Set(
    report.reservas.map(item => item.sucursal).filter((value): value is string => Boolean(value)),
  ));
  return selectedBranch || (branches.length === 1 ? branches[0] : 'Sucursales autorizadas');
};

export const createArrivalsReportPdf = (
  report: TodayArrivalsReportData,
  selectedBranch?: string,
): jsPDF => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const branch = branchName(report, selectedBranch);

  doc.setFillColor(...BRAND_BLUE);
  doc.rect(0, 0, pageWidth, 23, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text('REPORTE DE LLEGADAS', 14, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Fecha: ${report.fecha}  |  Sucursal: ${branch}`, 14, 17);

  doc.setTextColor(...BRAND_NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`${report.total_clientes} clientes`, 14, 31);
  doc.text(`${report.total_reservas} reservas`, 55, 31);
  if (report.advertencia) {
    doc.setTextColor(154, 52, 18);
    doc.setFontSize(8);
    doc.text(report.advertencia, 14, 36);
  }

  const body = report.reservas.map(reservation => [
    reservation.fecha_ocurrencia || report.fecha,
    reservation.codigo,
    `${reservation.hora_inicio || '-'}${reservation.hora_fin ? ` - ${reservation.hora_fin}` : ''}`,
    reservation.clientes.map(person => `${person.nombre} (${person.tipo})`).join('\n'),
    reservation.servicio || '-',
    reservation.espacio || '-',
    reservation.sucursal || '-',
    `Bs ${reservation.total}`,
    `Bs ${reservation.pagado}`,
    `Bs ${reservation.deuda}`,
  ]);

  autoTable(doc, {
    startY: report.advertencia ? 41 : 36,
    head: [['Fecha', 'Código', 'Horario', 'Clientes', 'Servicio', 'Espacio', 'Sucursal', 'Total', 'Pagado', 'Deuda']],
    body,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 7, cellPadding: 2, textColor: BRAND_NAVY, lineColor: [217, 226, 236] },
    headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 24 },
      2: { cellWidth: 27 },
      3: { cellWidth: 45 },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right' },
    },
    didDrawPage: data => {
      const pageNumber = doc.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(`Página ${pageNumber}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 7, { align: 'right' });
      if (data.pageNumber > 1) {
        doc.setFontSize(9);
        doc.setTextColor(...BRAND_NAVY);
        doc.text(`Llegadas del ${report.fecha} · ${branch}`, 14, 10);
      }
    },
    margin: { top: 15, right: 12, bottom: 13, left: 12 },
  });

  return doc;
};

export const createArrivalsReportWorkbook = async (
  report: TodayArrivalsReportData,
  selectedBranch?: string,
): Promise<ExcelJS.Workbook> => {
  const { default: ExcelJSRuntime } = await import('exceljs');
  const branch = branchName(report, selectedBranch);
  const workbook = new ExcelJSRuntime.Workbook();
  workbook.creator = 'Comarca Cowork';
  workbook.created = new Date();
  const sheet = workbook.addWorksheet('Llegadas', {
    views: [{ state: 'frozen', ySplit: 6 }],
    pageSetup: {
      orientation: 'landscape',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
      margins: { left: 0.25, right: 0.25, top: 0.45, bottom: 0.45, header: 0.2, footer: 0.2 },
    },
  });

  sheet.columns = [14, 18, 12, 12, 22, 18, 24, 28, 12, 18, 15, 16, 13, 13, 13]
    .map(width => ({ width }));
  sheet.addRow(['REPORTE DE LLEGADAS']);
  sheet.addRow(['Fecha', report.fecha]);
  sheet.addRow(['Sucursal', branch]);
  sheet.addRow(['Resumen', `${report.total_clientes} clientes · ${report.total_reservas} reservas`]);
  sheet.addRow(report.advertencia ? ['Advertencia', report.advertencia] : []);
  sheet.addRow(['Fecha', 'Código', 'Hora inicio', 'Hora fin', 'Servicio', 'Espacio', 'Cliente', 'Correo', 'Tipo', 'Sucursal', 'Estado', 'Estado pago', 'Total (Bs)', 'Pagado (Bs)', 'Deuda (Bs)']);

  report.reservas.forEach(reservation => {
    const clients = reservation.clientes.length ? reservation.clientes : [{ nombre: '-', email: '-', tipo: 'titular' as const, id: 0 }];
    sheet.addRow([
      reservation.fecha_ocurrencia || report.fecha,
      reservation.codigo,
      reservation.hora_inicio || '',
      reservation.hora_fin || '',
      reservation.servicio || '',
      reservation.espacio || '',
      clients.map(person => person.nombre).join('\n'),
      clients.map(person => person.email).join('\n'),
      clients.map(person => person.tipo).join('\n'),
      reservation.sucursal || '',
      reservation.estado,
      reservation.estado_pago,
      Number(reservation.total),
      Number(reservation.pagado),
      Number(reservation.deuda),
    ]);
  });

  sheet.mergeCells('A1:O1');
  sheet.autoFilter = { from: 'A6', to: `O${Math.max(sheet.rowCount, 6)}` };
  sheet.pageSetup.printArea = `A1:O${Math.max(sheet.rowCount, 6)}`;
  sheet.getRow(1).height = 28;
  sheet.getRow(5).height = 8;
  sheet.getRow(6).height = 24;

  const title = sheet.getCell('A1');
  title.font = { name: 'Arial', bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.getRow(6).eachCell(cell => {
    cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
  });

  for (let rowNumber = 7; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const row = sheet.getRow(rowNumber);
    row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      cell.font = { name: 'Arial', size: 9, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowNumber % 2 ? 'FFFFFFFF' : 'FFF8FAFC' } };
      cell.alignment = { vertical: 'top', wrapText: true };
      cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
      if (columnNumber >= 13) cell.numFmt = '#,##0.00';
    });
  }

  ['A2', 'A3', 'A4'].forEach(address => {
    const cell = sheet.getCell(address);
    cell.font = { name: 'Arial', bold: true, color: { argb: 'FF007FC8' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${LIGHT_BLUE}` } };
  });

  return workbook;
};

export const exportArrivalsReportPdf = (report: TodayArrivalsReportData, selectedBranch?: string) => {
  const suffix = cleanFilePart(branchName(report, selectedBranch));
  createArrivalsReportPdf(report, selectedBranch).save(`reporte_llegadas_${report.fecha}_${suffix}.pdf`);
};

export const exportArrivalsReportExcel = async (report: TodayArrivalsReportData, selectedBranch?: string) => {
  const suffix = cleanFilePart(branchName(report, selectedBranch));
  const workbook = await createArrivalsReportWorkbook(report, selectedBranch);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `reporte_llegadas_${report.fecha}_${suffix}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
